import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { useQueryClient } from '@tanstack/react-query';
import { Loader } from '@mews-commander/ui';
import { lt, validateWithZod } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { commanderForm } from '@mews-commander/legacy-form';
import {
    createRateGroupEditFormValidator,
    RateGroupForm,
    RateGroupFormProps,
    createRateGroupEditFormModel,
    transformRateGroupDto,
    RateGroupEditFormModel,
} from '../RateGroupForm';
import { getRateGroupsGetAllQueryKey, useRateGroupsGetAllQuery } from '../useRateGroupsGetAllQuery';
import { useRateGroupUpdateMutation } from '../useRateGroupUpdateMutation';

const ReduxRateGroupEditForm = pipe(
    RateGroupForm,
    commanderForm<RateGroupEditFormModel, RateGroupFormProps>({
        submitLabel: lt('Save'),
        submitButtonProps: {
            variant: 'primary',
        },
    })
);

export interface RateGroupFormEditProps {
    form: string;
    serviceId: string;
    rateGroupId: string;
}

export const RateGroupEditForm = ({ serviceId, rateGroupId, ...props }: RateGroupFormEditProps) => {
    const queryCLient = useQueryClient();
    const { mutateAsync } = useRateGroupUpdateMutation();

    const currentEnterpriseId = useCurrentEnterpriseId();
    const queryParams = {
        enterpriseIds: [currentEnterpriseId],
        rateGroupIds: [rateGroupId],
    };
    const { isLoading: isRateGroupsLoading, data: rateGroupInitial } = useRateGroupsGetAllQuery(queryParams, {
        select: (data) => data.rateGroups.find((r) => r.id === rateGroupId),
    });

    const defaultLanguageCode = useCommanderSelector<string>(getCurrentEnterpriseDefaultLanguageCode);

    const model = useMemo(
        () =>
            createRateGroupEditFormModel({
                languageCode: defaultLanguageCode,
            }),
        [defaultLanguageCode]
    );

    if (isRateGroupsLoading) {
        return <Loader />;
    }

    if (!rateGroupInitial) {
        return null;
    }

    const initialValues = transformRateGroupDto(rateGroupInitial);

    const validateSchema = createRateGroupEditFormValidator(model, initialValues);
    const validate = validateWithZod(validateSchema);

    const handleSubmit = async (formValues: RateGroupEditFormModel) => {
        const params = validateSchema.parse(formValues);
        return mutateAsync(params, {
            onSuccess: (result) => {
                queryCLient.setQueryData(getRateGroupsGetAllQueryKey(queryParams), result);
            },
            // eslint-disable-next-line @mews/no-empty-catch
        }).catch(() => {
            // The error is handled by redux-form
        });
    };

    return (
        <ReduxRateGroupEditForm
            {...props}
            isEditForm
            defaultLanguageCode={defaultLanguageCode}
            serviceId={serviceId}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validate={validate}
        />
    );
};
